import './formEntryNew.less';

import React, { CSSProperties, ReactNode } from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { QuestionCircleOutlined } from '@ant-design/icons';

import { ModelValidator, ValidatableModel, ValidationState } from 'framework/mobx-integration';
import { computed, makeAutoObservable, observable, runInAction } from 'mobx';
import { useStore } from 'core/react/useStore';
import b_ from 'b_';
import { AntTooltip } from '../ant/antTooltip';
import { FormLabelPosition, FormStore, useFormStore } from './form-store/formStore';

const b = b_.with('form-entry-new');

export class FormEntryProps<TModel extends object, TModelField extends keyof TModel> {
	label?: React.ReactNode;
	labelPosition?: FormLabelPosition;
	placeholder?: React.ReactNode;
	title?: 'string';
	children: React.ReactNode;
	width?: number | string;
	minWidth?: number | string;
	highlight?: boolean;

	className?: string;

	id?: string;
	style?: CSSProperties;

	model?: TModel;
	modelField?: TModelField;
	onChange?: (value: TModel[TModelField]) => void;
	value?: TModel[TModelField];

	// passed on to the input, after the entry has done its own work on the same event
	onFocus?: (ev: React.FocusEvent) => void;
	onBlur?: (ev: React.FocusEvent) => void;

	headerAdditionalContent?: React.ReactNode;

	disabled?: boolean;

	validator?: ModelValidator<TModel>;
	// looks up the validator under this key instead of `modelField` — for fields whose value lives at a nested
	// path (e.g. CEAlgorithm's percentile/days) but whose validation rule is registered under a compound key
	// (e.g. `${property.id}.percentile`) on the shared form-level validator.
	validatorField?: string;

	changeOnBlur?: boolean;
	alwaysWithValue?: boolean;

	noUi?: boolean;

	hasPlaceholder?: boolean;

	hideRequiredMark?: boolean;

	oldStyle?: boolean;

	//if you do not want Form entry to bind the first children to a provided model but only want to show validation result.
	// This might be useful when there are several inputs inside form entry
	validationOnly?: boolean;

	constructor() {
		makeAutoObservable(this, {
			children: observable.ref,
			headerAdditionalContent: observable.ref,
			label: observable.ref,
		});
	}
}

class FormEntryNewStore<TModel extends ValidatableModel<TModel> | object, TModelField extends keyof TModel> {
	focused: boolean;
	props: FormEntryProps<TModel, TModelField> = new FormEntryProps<TModel, TModelField>();
	tempValue: TModel[TModelField] = null;
	initialValueChanged: boolean = false;

	constructor(private form: FormStore) {
		makeAutoObservable(this, {
			validationState: computed({ keepAlive: true }),
		});
	}

	// the form's one serves only the entries naming a field of it
	get model() {
		return this.props.model ?? (this.props.modelField != null ? (this.form.model as TModel) : undefined);
	}

	get validator() {
		if (this.props.validator) return this.props.validator;

		if (this.props.model == null && this.form.validator != null) {
			return this.form.validator as ModelValidator<TModel>;
		}

		const { model } = this;
		if (model == null) return null;

		return 'validator' in model ? model.validator : null;
	}

	get labelPosition() {
		return this.props.labelPosition ?? this.form.labelPosition;
	}

	get compact() {
		return this.form.appearance == 'compact';
	}

	// a floating one has no border to rise onto over a compact form's field, so it is only the field's placeholder there
	get labelShown() {
		if (!this.props.label || this.labelPosition == 'none') return false;

		return !(this.labelPosition == 'floating' && this.compact);
	}

	get fieldValidator() {
		return this.validator?.getFieldValidator(this.props.validatorField ?? (this.props.modelField as string));
	}

	get hasValidation() {
		return this.fieldValidator != null;
	}

	get required() {
		if (this.fieldValidator == null) return false;

		return this.fieldValidator.requiredValidationState != ValidationState.Valid;
	}

	get requiredMarkShown() {
		return this.required && !this.props.hideRequiredMark;
	}

	get validationState() {
		if (this.fieldValidator == null) return ValidationState.Valid;

		return this.fieldValidator.revalidated && (this.initialValueChanged || this.validator.submitted)
			? this.fieldValidator.validationState
			: ValidationState.Valid;
	}

	get validationMessage() {
		return this.validationState != ValidationState.Valid
			? (this.validator?.getErrors(this.props.modelField).join() ?? '')
			: '';
	}

	get value() {
		if (this.props.value != null) return this.props.value;

		if (this.model == null) return undefined;

		if (this.focused && this.props.changeOnBlur) {
			return this.tempValue;
		}

		return this.model[this.props.modelField];
	}

	get hasValue() {
		return !!(
			(Array.isArray(this.value)
				? this.value.length > 0
				: (this.value as never as number) === 0 || (this.value as never as boolean) === false
					? true
					: !!this.value) || this.props.alwaysWithValue
		);
	}

	get onChange() {
		if (this.props.onChange) return this.props.onChange;

		if (this.props.validationOnly) return undefined;

		if (this.props.changeOnBlur) {
			return (value: TModel[TModelField]) => (this.tempValue = value);
		}

		const { model } = this;
		if (!model) return undefined;

		return (value: TModel[TModelField]) => runInAction(() => (model[this.props.modelField] = value));
	}

	get style() {
		const fields = ['width', 'minWidth'] as const;
		// the stylesheet takes it for the label's column and for the message under the field
		const labelWidth =
			this.labelPosition == 'left' ? { '--form-entry-label-width': `${this.form.labelWidth}px` } : {};

		const result: CSSProperties = {
			...(this.props.style ?? {}),
			...labelWidth,
		};
		if (fields.every((f) => this.props[f] == null)) return result;

		fields.forEach((f) => {
			if (Number.isInteger(this.props[f])) {
				result[f] = this.props[f] + 'px';
			} else {
				result[f] = this.props[f];
			}
		});

		return result;
	}

	// a boolean: it goes into a class modifier, where a text would be split into classes of its own
	get displayPlaceholder() {
		return !!(this.props.hasPlaceholder && this.props.placeholder);
	}

	getProcessedChildren() {
		const children = React.Children.toArray(this.props.children);
		if (!React.isValidElement(children[0])) return children;

		const { onFocus, onBlur, readOnly } = (children[0].props ?? {}) as {
			onFocus?: (ev: React.FocusEvent) => void;
			onBlur?: (ev: React.FocusEvent) => void;
			readOnly?: boolean;
		};
		// cloned onto whatever element type the first child happens to be (cloneElement's own props
		// type is `any`-driven there too), so there's no real type to constrain this to more tightly
		const propsOverrides: Record<string, unknown> = {
			onFocus: (ev: React.FocusEvent) => {
				if (readOnly) return;

				this.tempValue = this.value;

				this.focused = true;
				onFocus?.(ev);
				this.props.onFocus?.(ev);
			},
			onBlur: (ev: React.FocusEvent) => {
				this.focused = false;

				if (!!this.model && this.model[this.props.modelField] != this.tempValue) {
					this.initialValueChanged = true;
				}
				if (!!this.model && this.props.changeOnBlur) {
					this.model[this.props.modelField] = this.tempValue;
					this.tempValue = null;
				}
				onBlur?.(ev);
				this.props.onBlur?.(ev);
			},
		};

		if (this.props.validationOnly !== true) {
			if (this.value !== undefined) {
				propsOverrides.value = this.value;
			}

			if (this.onChange !== undefined) {
				propsOverrides.onChange = this.onChange;
			}

			if (this.props.disabled !== undefined) {
				propsOverrides.disabled = this.props.disabled;
			}
		}

		if (this.labelPosition != 'floating') {
			if (this.props.placeholder) {
				propsOverrides.placeholder = this.props.placeholder;
			}
		} else if (this.compact) {
			propsOverrides.placeholder = this.props.placeholder ?? (this.props.hasPlaceholder ? '' : this.props.label);
		} else {
			propsOverrides.placeholder = this.props.placeholder || undefined;

			if (
				(this.props.label && !this.focused && !this.props.hasPlaceholder) ||
				(this.hasValue && !this.props.hasPlaceholder)
			) {
				propsOverrides.placeholder = '';
			}
		}

		if (this.labelPosition == 'inside' && this.labelShown) {
			propsOverrides.prefix = (
				<span className={b('inside-label')}>
					{this.props.label}
					{this.requiredMarkShown && ' *'}
				</span>
			);
		}

		//We are assuming there that the first child element is an input so we can pass value and onChange
		children[0] = React.cloneElement(children[0], propsOverrides);

		return children;
	}
}

export const FormEntryNew = observer(
	<TModel extends ValidatableModel<TModel> | object, TModelField extends keyof TModel>(
		props: FormEntryProps<TModel, TModelField>,
	) => {
		const form = useFormStore();
		const store = useStore(() => new FormEntryNewStore<TModel, TModelField>(form), {
			props: props,
		});

		const blockClasses: { [index: string]: boolean | string } = {
			'focused': store.focused,
			'disabled': props.disabled,
			'has-validation': store.hasValidation,
			'required': store.required,
			'validation-state': store.validationState,
			'has-value': !!(store.hasValue || store.displayPlaceholder),
			'highlight': props.highlight,
			'label-position': store.labelPosition,
			'compact': store.compact,
		};

		const rootClasses = classnames(b(blockClasses), props.className);

		if (props.noUi) {
			return store.getProcessedChildren();
		}

		return (
			<div id={props.id} className={rootClasses} style={{ ...store.style }} title={props.title}>
				{store.labelShown && store.labelPosition != 'inside' && (
					<div className={b('label')} title={typeof props.label == 'string' ? props.label : undefined}>
						{store.labelPosition != 'floating' || store.hasValue || store.displayPlaceholder
							? props.label
							: (props.placeholder ?? (props.hasPlaceholder ? '' : props.label))}
						{store.requiredMarkShown && ' *'}
					</div>
				)}

				<div className={b('field')}>{store.getProcessedChildren()}</div>

				{props.headerAdditionalContent && (
					<div className={b('additional-content')}>{props.headerAdditionalContent}</div>
				)}

				{store.validationMessage && <p className={b('validation-message')}>{store.validationMessage}</p>}
			</div>
		);
	},
);

export type HelpIconProps = {
	tooltip?: string | ReactNode;
	className?: string;
	style?: React.CSSProperties;
};

export const HelpIconThemed = observer((props: HelpIconProps) => {
	const icon = (
		<QuestionCircleOutlined className={classnames(props.className, b('help-icon-themed'))} style={props.style} />
	);
	if (props.tooltip) {
		return <AntTooltip title={props.tooltip}>{icon}</AntTooltip>;
	}
	return icon;
});

export const HelpIcon = observer((props: HelpIconProps) => {
	return <HelpIconThemed {...props} />;
	// const cn = classnames(props.className, b('help-icon'), 'glyphicons', 'question-sign');
	// const icon = <i className={cn} style={props.style} />;
	// if (props.tooltip) {
	// 	return <AntTooltip title={props.tooltip}>{icon}</AntTooltip>;
	// }
	// return icon;
});

export function useGenericModel<T>(getCallback: () => T, setCallback: (value: T) => void, deps: object[]) {
	return React.useMemo(() => {
		return new GenericModel(getCallback, setCallback);
	}, deps);
}

class GenericModel<T> {
	constructor(
		private getCallback: () => T,
		private setCallback: (value: T) => void,
	) {
		makeAutoObservable(this);
	}

	get value() {
		return this.getCallback();
	}

	set value(value: T) {
		this.setCallback(value);
	}
}
